/**
 * CampaignFlowsTab
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { LoadingSpinner } from '@/components/ui';
import type { Flow } from '@/hooks';
import { FlowConfigTab } from '@/features/flows/detail/components';
import { FlowOptimizerButton } from '@/features/flow-optimizer';
import { SnapshotsButton } from '@/features/flow-optimizer/snapshots';
import { flowActionsOf, optimizerProviderOf } from '@/features/campaigns/tracker/campaign-tracker-access';
import { TrackerReadOnlyNote } from '@/features/campaigns/tracker/TrackerReadOnlyNote';
import { campaignFlowsKey, dispatchTracker } from '@/hooks/campaign-tracker';
import CampaignFlowSelector from './CampaignFlowSelector';
import type { CampaignProvider } from '@/hooks/voluum/useVoluum';

interface CampaignFlowsTabProps {
  loading: boolean;
  campaignId?: string;
  campaignName?: string;
  provider?: CampaignProvider;
  preferredDateRangePreset?: string;
  customFrom?: string;
  customTo?: string;
  onActiveRangeChange?: (range: { preset: string; from?: string; to?: string }) => void;
  flows: Flow[];
}

export const CampaignFlowsTab: FC<CampaignFlowsTabProps> = ({
  flows,
  loading,
  campaignId,
  campaignName,
  provider = 'voluum',
  preferredDateRangePreset,
  customFrom,
  customTo,
  onActiveRangeChange,
}) => {
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');

  useEffect(() => {
    setSelectedFlowId('');
  }, [campaignId]);

  useEffect(() => {
    if (!selectedFlowId && flows.length > 0) {
      setSelectedFlowId(flows[0].id);
    }
  }, [flows, selectedFlowId]);

  const selectedFlow = useMemo(
    () => flows.find((f) => f.id === selectedFlowId) ?? flows[0],
    [flows, selectedFlowId],
  );
  // A tracker with the Flow Optimizer (registry capability): its button. One
  // with flow edits only: Flow history (snapshots) + a "comes next" note.
  const optimizer = optimizerProviderOf(provider);
  const actions = flowActionsOf(provider);
  const queryClient = useQueryClient();
  const refetchFlows = useCallback(() => {
    if (campaignId) void queryClient.invalidateQueries({ queryKey: campaignFlowsKey(provider, campaignId) });
  }, [campaignId, provider, queryClient]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingSpinner />
      </div>
    );
  }

  if (flows.length === 0) {
    return (
      <div className="px-4 py-10 text-center text-muted-foreground">
        <Layers className="mx-auto mb-3 h-10 w-10 opacity-40" aria-hidden />
        <p className="text-sm">No flows found for this campaign.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 border-b border-border/40 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <CampaignFlowSelector
          flows={flows}
          selectedFlowId={selectedFlowId}
          selectedFlowName={selectedFlow?.name}
          onSelect={setSelectedFlowId}
        />
        {campaignId && selectedFlowId ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            {optimizer ? (
              <FlowOptimizerButton
                campaignId={campaignId}
                flowId={selectedFlowId}
                provider={optimizer}
                campaignName={campaignName}
                flowName={selectedFlow?.name}
              />
            ) : null}
            {actions === 'snapshots' ? (
              <SnapshotsButton
                flowId={selectedFlowId}
                provider={dispatchTracker(provider)}
                onRestored={refetchFlows}
              />
            ) : null}
            {optimizer ? null : <TrackerReadOnlyNote tracker={dispatchTracker(provider)} />}
          </div>
        ) : null}
      </div>

      {!selectedFlowId ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">Select a flow to continue.</p>
      ) : (
        <FlowConfigTab
          key={selectedFlowId}
          flowId={selectedFlowId}
          campaignId={campaignId}
          provider={provider}
          preferredDateRangePreset={preferredDateRangePreset}
          customFrom={customFrom}
          customTo={customTo}
          onActiveRangeChange={onActiveRangeChange}
          isLoading={false}
          flow={selectedFlow}
          pathsGridInCard
        />
      )}
    </div>
  );
};

export default CampaignFlowsTab;
